/**
 * Utility functions for OpenGraph URL handling
 */

/**
 * Extracts URLs from text using a comprehensive regex pattern
 * @param {string} text - The text to extract URLs from
 * @returns {Array} Array of URL objects with url, start, and end positions
 */
export const extractUrlsFromText = (text) => {
  if (!text) return [];

  // More comprehensive URL regex that matches various URL patterns
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;
  const urls = [];
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    urls.push({
      url: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  return urls;
};

/**
 * Checks if text contains only URLs (with optional whitespace)
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains only URLs
 */
export const isTextOnlyUrls = (text) => {
  if (!text) return false;

  const urls = extractUrlsFromText(text);
  if (urls.length === 0) return false;

  // Remove all URLs from the text
  let textWithoutUrls = text;
  for (let i = urls.length - 1; i >= 0; i--) {
    const url = urls[i];
    textWithoutUrls =
      textWithoutUrls.slice(0, url.start) + textWithoutUrls.slice(url.end);
  }

  // Check if remaining text is only whitespace
  return textWithoutUrls.trim().length === 0;
};

/**
 * Collapses TipTap / HTML descriptions to plain text for visibility checks.
 * Empty paragraphs, &lt;br&gt;, whitespace-only → "".
 */
export const getPlainTextFromRichHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Filters out URLs from text when they're the only content
 * @param {string} text - The original text
 * @param {Array} openGraphData - Array of OpenGraph data objects
 * @returns {string} Filtered text with URLs removed if appropriate
 */
export const filterUrlsFromText = (text, openGraphData) => {
  if (!text || !openGraphData || openGraphData.length === 0) {
    return text;
  }

  // If text contains only URLs, remove them
  if (isTextOnlyUrls(text)) {
    return '';
  }

  // If text contains URLs plus other content, keep everything
  return text;
};

/**
 * Determines if URLs should be hidden from display
 * @param {string} text - The post text
 * @param {Array} openGraphData - Array of OpenGraph data objects
 * @returns {boolean} True if URLs should be hidden
 */
export const shouldHideUrls = (text, openGraphData) => {
  if (!text || !openGraphData || openGraphData.length === 0) {
    return false;
  }

  return isTextOnlyUrls(text);
};
